<template>
  <div>
    <v-container fluid>
      <v-toolbar flat class="d-print-none pb-10">
        <v-btn icon title="Previous week" @click="moveWeek(-1)">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <v-text-field
          :value="weekFormatted"
          :loading="loading"
          label="Order week"
          hide-details
          readonly
          append-icon="mdi-calendar"
          style="max-width: 20rem"
          data-cy="week-field"
          @click="showDatePicker=true"
        />
        <v-btn icon title="Next week" @click="moveWeek(1)">
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
        <v-spacer/>
        <!-- a toggle, in the middle of the page under the app bar's page button and the same size -->
        <v-btn
          outlined
          text
          class="help-toggle"
          :input-value="showHelp"
          :aria-pressed="showHelp ? 'true' : 'false'"
          data-cy="help-button"
          @click="setHelpShown(!showHelp)"
        >
          <v-icon left>mdi-help-circle-outline</v-icon>
          Help
        </v-btn>
        <CustomerAutocomplete
          style="min-width: 30rem"
          v-model="customer"
        />
        <v-dialog v-model="showDatePicker" width="290px">
          <v-date-picker
            :value="week"
            no-title
            first-day-of-week="0"
            @input="pickWeekOf"
          />
        </v-dialog>
      </v-toolbar>

      <v-alert
        v-if="showHelp"
        outlined
        dismissible
        color="info"
        icon="mdi-help-circle-outline"
        class="d-print-none"
        data-cy="help"
      >
        <template v-slot:close>
          <v-btn small outlined color="info" class="ml-4" data-cy="close-help" @click="setHelpShown(false)">
            Close help
          </v-btn>
        </template>
        <div class="font-weight-bold mb-1">How to use this page</div>
        <ul>
          <li>
            Choose a customer and the order week. Their invoice for the week is shown, a card per delivery. A
            household's invoice lists every profile's meals.
          </li>
          <li>
            <strong>Cancel:</strong> tick the meals, or <em>Select whole delivery</em>, choose how many of a line
            in the Cancel column, then <em>Cancel meals</em> at the bottom of the page.
          </li>
          <li><strong>Quantity:</strong> sets how many of one line are ordered. 0 cancels it.</li>
          <li>
            <strong>Replace:</strong> puts another meal in a line's place. A plan meal takes a substitute from
            another stream: tick <em>Allergy substitution</em> to charge it as the plan's own meal. A box meal
            takes any meal of the same delivery.
          </li>
          <li>
            <strong>Add meal:</strong> a plan meal, a substitute or a snack/extra. For a customer on a box plan,
            a meal for the box of a delivery.
          </li>
          <li>
            A box that gains or loses meals becomes the smallest size on the plan that fits them, and is priced
            at that size, unless <em>Keep the box size</em> is ticked.
          </li>
          <li><strong>View menu:</strong> the week's menu for the customer, or the box catalog on a box plan.</li>
          <li>
            Each change is made on the customer's order, so it changes the quantities the kitchen makes, and
            the invoice is worked out again: the old and new total are shown. An order can be changed until a
            week after it was billed.
          </li>
          <li>
            Once a week is billed, a change here does not refund or charge the customer: do that on the
            <em>Stripe Comparison</em> page.
          </li>
          <li>The customer gets no email unless <em>Send the customer the order-updated email</em> is ticked.</li>
        </ul>
      </v-alert>

      <v-alert v-if="!customerId" outlined type="info">
        Choose a customer to see their invoice for the week, and to cancel, add or replace meals on it.
      </v-alert>

      <v-card v-if="customer && weekData" class="ma-4" data-cy="invoice-card">
        <v-card-title>
          <CustomerHeader :customer="customer"/>
          <v-spacer/>
          <v-btn v-if="canEdit" class="mr-2" small color="primary" data-cy="add-meal" @click="openAddMeal">
            Add meal
          </v-btn>
          <v-btn class="mr-2" small color="info" data-cy="view-menu" @click="openMenu">View menu</v-btn>
          <v-btn outlined small :to="stripeComparisonRoute">Stripe Comparison</v-btn>
        </v-card-title>

        <v-card-text class="pb-0" data-cy="profiles">
          <div v-for="profile in profiles" :key="profile.uid" data-cy="profile">
            <span v-if="profiles.length > 1" class="font-weight-medium">{{ nameOf(profile) }}:</span>
            plan {{ profile.plan || 'none' }} ( {{ profile.stream || 'no stream' }} )
            <v-chip v-if="profile.ordersAsBox" x-small outlined class="ml-1">orders a box per delivery</v-chip>
            <v-chip v-if="profile.planChangingFrom" x-small outlined color="warning" class="ml-1">
              plan changing, was {{ profile.planChangingFrom }}
            </v-chip>
          </div>
        </v-card-text>

        <v-card-text>
          <v-alert v-if="isHouseholdInvoice" dense outlined type="info">
            This customer is billed with their household: the invoice below is the main account's and lists every
            profile's meals.
          </v-alert>
          <v-alert
            v-for="profile in pausedProfiles"
            :key="`pause-${profile.uid}`"
            dense
            outlined
            type="warning"
            data-cy="pause-alert"
          >
            Pause active for {{ nameOf(profile) }}: {{ profile.pause.map(date => dateFormatted(date)).join(' to ') }}.
            Meals inside the pause are not on the invoice.
          </v-alert>
          <v-alert v-if="invoice && invoice.warning" dense outlined type="warning">{{ invoice.warning }}</v-alert>
          <v-alert v-if="weekData.warning" dense outlined type="warning">{{ weekData.warning }}</v-alert>
          <v-alert v-if="refusal" dense outlined type="error" data-cy="refusal">
            Nothing was changed: {{ refusal }}. The invoice has been reloaded.
          </v-alert>

          <v-alert v-if="lastChange" dense text type="success" data-cy="change-alert">
            <v-row align="center" no-gutters>
              <v-col>
                {{ lastChange.summary }}.
                The invoice total (with tax) was {{ formatCurrency(lastChange.totalBefore) }} and is now
                {{ formatCurrency(lastChange.totalAfter) }}.
                <span v-if="weekData.isBilled">
                  This week has been billed: {{ lastChangeIsRefund ? 'refund' : 'apply' }} the difference on the
                  Stripe comparison page.
                </span>
              </v-col>
              <v-col v-if="weekData.isBilled" cols="auto">
                <v-btn small color="primary" :to="stripeComparisonRoute">
                  {{ lastChangeIsRefund ? 'Refund in Stripe' : 'Adjust in Stripe' }}
                </v-btn>
              </v-col>
            </v-row>
          </v-alert>

          <v-alert v-if="!weekData.canChange" dense outlined type="warning" data-cy="closed-alert">
            This week was billed on {{ formatDateTime(weekData.billedAt) }}, more than a week ago. Its order can no
            longer be changed here.
          </v-alert>
          <v-alert v-else-if="weekData.isBilled" dense outlined type="warning">
            This week was billed on {{ formatDateTime(weekData.billedAt) }}. A change here changes the customer's
            order, so the quantities the kitchen makes, and the Fed invoice. It does not refund or charge the
            customer: do that on the Stripe comparison page afterwards.
          </v-alert>

          <v-alert v-if="deliveryGroups.length === 0" outlined type="info" data-cy="nothing-ordered">
            Nothing is ordered for the week of {{ weekFormatted }}.
          </v-alert>
        </v-card-text>

        <v-card
          v-for="delivery in deliveryGroups"
          :key="delivery.shipment"
          outlined
          class="mx-4 mb-4"
          :data-cy="`delivery-${delivery.deliveryDate}`"
        >
          <v-card-title class="text-subtitle-1">
            <div>
              <div class="font-weight-bold">Delivery {{ dateFormatted(delivery.deliveryDate) }}</div>
              <div class="text-body-2">{{ delivery.address ? formatAddress(delivery.address) : '' }}</div>
            </div>
            <v-chip v-if="delivery.isPastEditDeadline" small outlined color="warning" class="ml-4">
              Edit deadline passed {{ formatDateTime(delivery.editDeadline) }}
            </v-chip>
            <v-chip v-else-if="delivery.editDeadline" small outlined class="ml-4">
              Customer can edit until {{ formatDateTime(delivery.editDeadline) }}
            </v-chip>
            <v-spacer/>
            <v-btn
              v-if="canEdit && delivery.lines.length > 0"
              small
              outlined
              color="error"
              data-cy="select-delivery"
              @click="selectDelivery(delivery)"
            >
              Select whole delivery
            </v-btn>
          </v-card-title>

          <v-simple-table dense class="order-lines">
            <thead>
            <tr>
              <th v-if="canEdit" class="select-column"></th>
              <th>Meal ID</th>
              <th class="text-right">Quantity</th>
              <th v-if="canEdit">Cancel</th>
              <th>Meal</th>
              <th>Time of day</th>
              <th>Date</th>
              <th>Stream</th>
              <th>Bin</th>
              <th>Once</th>
              <th>Sub</th>
              <th>For</th>
              <th class="text-right">Each</th>
              <th class="text-right">Total</th>
              <th v-if="canEdit"></th>
            </tr>
            </thead>
            <tbody>
            <tr
              v-for="line in delivery.lines"
              :key="line.key"
              :class="{'line-selected': isSelected(line)}"
              data-cy="order-line"
            >
              <td v-if="canEdit" class="select-column">
                <v-simple-checkbox
                  :value="isSelected(line)"
                  :ripple="false"
                  color="error"
                  @input="toggleLine(line)"
                />
              </td>
              <td>{{ line.meal_id }}</td>
              <td class="text-right" data-cy="line-quantity">{{ line.quantity }}</td>
              <td v-if="canEdit">
                <v-select
                  v-if="isSelected(line) && line.quantity > 1"
                  :value="cancelQuantities[line.key]"
                  :items="quantityChoices(line)"
                  dense
                  hide-details
                  class="cancel-quantity"
                  data-cy="cancel-quantity"
                  @input="setCancelQuantity(line, $event)"
                />
                <span v-else-if="isSelected(line)">{{ cancelQuantities[line.key] }}</span>
              </td>
              <td class="meal-column">
                {{ line.description }}
                <span v-if="line.order_type !== 'full'" class="grey--text">
                  {{ formatOrderType(line.order_type) }}
                  <template v-if="line.box_size">of {{ line.box_size }}</template>
                </span>
              </td>
              <td>{{ line.tod }}</td>
              <td class="text-no-wrap">{{ dateFormatted(line.date, {formatString: 'ddd MMM D'}) }}</td>
              <td>{{ line.stream }}</td>
              <td>{{ line.bin }}</td>
              <td>{{ line.only_once ? 'once' : '' }}</td>
              <td>{{ substitutionLabel(line) }}</td>
              <td class="text-no-wrap">{{ [line.first_name, line.last_name].filter(s => !!s).join(' ') }}</td>
              <td class="text-right">{{ formatCurrency(line.unit_amount) }}</td>
              <td class="text-right">{{ formatCurrency(line.total) }}</td>
              <td v-if="canEdit" class="text-no-wrap">
                <v-btn x-small outlined class="mr-1" data-cy="change-quantity" @click="openQuantityDialog(line)">
                  Quantity
                </v-btn>
                <v-btn
                  v-if="canReplace(line)"
                  x-small
                  outlined
                  data-cy="replace-meal"
                  @click="openReplaceMeal(line)"
                >
                  Replace
                </v-btn>
              </td>
            </tr>
            <tr v-for="(charge, i) in delivery.charges" :key="`charge-${i}`" class="grey--text" data-cy="charge-line">
              <td v-if="canEdit" class="select-column"></td>
              <td></td>
              <td class="text-right">{{ charge.quantity }}</td>
              <td v-if="canEdit"></td>
              <td colspan="8">
                {{ charge.description }}
                <span v-if="canEdit">(worked out again when the meals change)</span>
              </td>
              <td class="text-right">{{ formatCurrency(charge.unit_amount) }}</td>
              <td class="text-right">{{ formatCurrency(charge.total) }}</td>
              <td v-if="canEdit"></td>
            </tr>
            </tbody>
          </v-simple-table>
        </v-card>

        <v-card v-if="otherLines.length > 0" outlined class="mx-4 mb-4">
          <v-card-title class="text-subtitle-1">Other lines</v-card-title>
          <v-simple-table dense>
            <tbody>
            <tr v-for="(item, i) in otherLines" :key="`other-${i}`">
              <td class="text-right">{{ item.quantity }}</td>
              <td>{{ item.description }}</td>
              <td class="text-right">{{ formatCurrency(item.total) }}</td>
            </tr>
            </tbody>
          </v-simple-table>
        </v-card>

        <v-card-text v-if="invoice" class="text-right" data-cy="invoice-totals">
          <div>Sub total {{ formatCurrency(invoice.total) }}</div>
          <div v-if="invoice.totalDiscount">Discount -{{ formatCurrency(invoice.totalDiscount) }}</div>
          <div v-if="invoice.totalTaxes">Taxes {{ formatCurrency(invoice.totalTaxes) }}</div>
          <div class="font-weight-bold">Total (w/ Tax) {{ formatCurrency(totalWithTax(invoice)) }}</div>
        </v-card-text>
      </v-card>
    </v-container>

    <v-footer v-if="selectedLines.length > 0" fixed class="d-print-none pl-10 pr-8" data-cy="selection-bar">
      {{ pluralMeals(selectedMealCount) }} selected, {{ formatCurrency(selectedAmount) }} before delivery, discounts
      and tax
      <v-spacer/>
      <v-btn text class="mr-2" @click="clearSelection">Clear selection</v-btn>
      <v-btn color="error" data-cy="review-cancel" @click="reviewCancel">
        Cancel {{ pluralMeals(selectedMealCount) }}…
      </v-btn>
    </v-footer>
    <!-- blank gap at the bottom to avoid the fixed footer hiding the totals -->
    <div class="ma-8"></div>

    <v-dialog v-model="showConfirmDialog" width="700px" :persistent="cancelling">
      <v-card data-cy="confirm-dialog">
        <v-card-title>Cancel {{ pluralMeals(review.mealCount) }}?</v-card-title>
        <v-card-text>
          <p v-if="customer">
            These are removed from the order of {{ customer.first_name }} {{ customer.last_name }} for the week of
            {{ weekFormatted }}, and the invoice is worked out again.
          </p>
          <v-simple-table dense>
            <tbody>
            <tr v-for="{line, quantity} in review.lines" :key="line.key">
              <td class="text-right text-no-wrap">{{ quantity }} of {{ line.quantity }}</td>
              <td>{{ line.description }}</td>
              <td class="text-no-wrap">{{ [line.first_name, line.last_name].filter(s => !!s).join(' ') }}</td>
              <td class="text-no-wrap">delivery {{ dateFormatted(line.ship_date, {formatString: 'ddd MMM D'}) }}</td>
            </tr>
            </tbody>
          </v-simple-table>
          <v-alert v-if="review.isPastEditDeadline" dense outlined type="warning" class="mt-4">
            Some of these meals are past their edit deadline and may already be in production or packed: let the
            kitchen know.
          </v-alert>
          <v-alert v-if="weekData && weekData.isBilled" dense outlined type="warning" class="mt-4">
            This week has been billed. The customer is not refunded until the adjustment is applied on the Stripe
            comparison page.
          </v-alert>
          <v-checkbox
            v-if="review.leavesPartOfABox"
            v-model="keepBoxSize"
            hide-details
            label="Keep the box size, so the meals left in the box keep their price"
            data-cy="keep-box-size"
          />
          <div v-if="review.leavesPartOfABox && !keepBoxSize" class="text-caption ml-8" data-cy="box-resize-note">
            The box becomes the smallest size on the plan that fits the meals left, and they are priced at that size.
          </div>
          <v-checkbox
            v-model="sendEmail"
            hide-details
            label="Send the customer the order-updated email"
            data-cy="send-email"
          />
        </v-card-text>
        <v-card-actions>
          <v-btn text :disabled="cancelling" @click="showConfirmDialog=false">Keep the meals</v-btn>
          <v-spacer/>
          <v-btn color="error" :loading="cancelling" data-cy="confirm-cancel" @click="cancelSelected">
            Cancel {{ pluralMeals(review.mealCount) }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showQuantityDialog" width="500px" :persistent="savingQuantity">
      <v-card v-if="quantityLine" data-cy="quantity-dialog">
        <v-card-title>Change quantity</v-card-title>
        <v-card-text>
          <p>
            {{ quantityLine.description }}
            ({{ dateFormatted(quantityLine.date, {formatString: 'ddd MMM D'}) }}<template v-if="quantityLine.tod">,
            {{ quantityLine.tod }}</template>): {{ quantityLine.quantity }} ordered.
          </p>
          <v-text-field
            v-model.number="newQuantity"
            label="New quantity"
            type="number"
            min="0"
            hint="0 cancels the meal"
            persistent-hint
            data-cy="new-quantity"
          />
          <v-checkbox
            v-if="quantityLine.order_type === 'box'"
            v-model="keepBoxSize"
            hide-details
            label="Keep the box size, so the meals in the box keep their price"
            data-cy="quantity-keep-box-size"
          />
          <div v-if="quantityLine.order_type === 'box' && !keepBoxSize" class="text-caption ml-8">
            The box becomes the smallest size on the plan that fits its meals, and they are priced at that size.
          </div>
          <v-checkbox
            v-model="sendEmail"
            hide-details
            label="Send the customer the order-updated email"
          />
        </v-card-text>
        <v-card-actions>
          <v-btn text :disabled="savingQuantity" @click="showQuantityDialog=false">Close</v-btn>
          <v-spacer/>
          <v-btn
            color="primary"
            :disabled="!canSaveQuantity"
            :loading="savingQuantity"
            data-cy="save-quantity"
            @click="changeQuantity"
          >
            Change to {{ newQuantity }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showMenuDialog" width="900px">
      <v-card v-if="menuProfile" data-cy="menu-dialog">
        <v-card-title>
          Menu for {{ nameOf(menuProfile) }} ({{ menuProfile.stream }})
          <v-spacer/>
          <v-btn icon @click="showMenuDialog=false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text style="max-height: 600px; overflow-y: auto;">
          <v-select
            v-if="profiles.length > 1"
            v-model="menuProfileUid"
            :items="profiles"
            :item-text="nameOf"
            item-value="uid"
            label="Menu of"
            data-cy="menu-profile"
          />
          <template v-if="menuProfile.ordersAsBox">
            <v-progress-linear v-if="loadingBoxCatalog" indeterminate/>
            <v-alert v-if="boxCatalogError" dense outlined type="error">{{ boxCatalogError }}</v-alert>
            <BoxCatalogDisplay
              v-if="boxCatalog"
              :catalog="boxCatalog"
              @copy-success="showMessage($event, 'success')"
              @copy-error="showMessage($event, 'error')"
            />
          </template>
          <MenuDisplay
            v-else-if="showMenuDialog"
            :uid="menuProfile.uid"
            :stream="menuProfile.stream"
            :date-from="week"
            :date-to="endOfWeek"
            :customer-plan="menuProfile"
            @copy-success="showMessage($event, 'success')"
            @copy-error="showMessage($event, 'error')"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="4000" bottom right>
      {{ snackbarText }}
    </v-snackbar>

    <OrderMealDialog
      v-if="weekData"
      v-model="showMealDialog"
      :week="week"
      :profiles="profiles"
      :default-uid="customerId"
      :line="lineToReplace"
      :saving="savingMeal"
      @add="addMeals"
      @replace="replaceMeal"
    />
  </div>
</template>

<script>
import moment from 'moment';
import api from '@/api';
import urlState from '@/router/urlState';
import CustomerAutocomplete from '@/components/CustomerAutocomplete.vue';
import CustomerHeader from '@/components/CustomerHeader';
import BoxCatalogDisplay from '@/components/BoxCatalogDisplay.vue';
import MenuDisplay from '@/components/MenuDisplay.vue';
import OrderMealDialog from '@/components/OrderMealDialog.vue';
import {dateFormatted, formatAddress, formatCurrency, formatOrderType} from '@/store/utils';
import {
  groupInvoiceByDelivery, leavesPartOfABox, linesWithoutDelivery, selectedCancelLines, toAddLine, toCancelLine
} from '@/utils/order-edit';

const DATE_FORMAT = moment.HTML5_FMT.DATE;

// the instructions stay closed in this browser once dismissed, until Help is clicked
const HELP_DISMISSED_KEY = 'order-edit-help-dismissed';

function isHelpDismissed() {
  try {
    return localStorage.getItem(HELP_DISMISSED_KEY) === 'true';
  } catch (e) {
    return false;
  }
}

// The week of the next delivery: deliveries are on Sunday and Wednesday, so
// from Wednesday on the next one opens the week after.
function weekOfNextDelivery() {
  return moment().add(4, 'days').startOf('week').format(DATE_FORMAT);
}

export default {
  name: 'OrderEdit',
  components: {BoxCatalogDisplay, CustomerAutocomplete, CustomerHeader, MenuDisplay, OrderMealDialog},
  mixins: [urlState],
  data() {
    return {
      readOnly: false,
      showHelp: !isHelpDismissed(),
      customer: null,
      customerId: null,
      week: weekOfNextDelivery(),
      // what the api answered for this customer and week: {invoice, deliveries, billedAt, isBilled, canChange, ...}
      weekData: null,
      loading: false,
      // "<customer>/<week>" of the week being loaded, while it is on its way
      loadingRequest: null,
      showDatePicker: false,
      // line key -> how many of the line to cancel
      cancelQuantities: {},
      showConfirmDialog: false,
      // what the confirm dialog lists and "Cancel" sends: the selection as it was when the dialog opened
      review: {lines: [], mealCount: 0, isPastEditDeadline: false, leavesPartOfABox: false},
      // a box that keeps some meals is made the size that fits them, unless this is ticked
      keepBoxSize: false,
      sendEmail: false,
      cancelling: false,
      // the add / replace meal dialog; lineToReplace is the invoice line it replaces the meal of, null to add
      showMealDialog: false,
      lineToReplace: null,
      savingMeal: false,
      // the change-quantity dialog and the invoice line it is open on
      showQuantityDialog: false,
      quantityLine: null,
      newQuantity: 0,
      savingQuantity: false,
      // the menu dialog: the profile whose menu it shows, and for a profile on a box plan the week's box catalog
      showMenuDialog: false,
      menuProfileUid: null,
      boxCatalog: null,
      loadingBoxCatalog: false,
      boxCatalogError: null,
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      // the last change made on this page: {summary, totalBefore, totalAfter}
      lastChange: null,
      // why the api refused the last change
      refusal: null,
    };
  },
  created() {
    // a customer or week in the url sets the value, and the watchers below load the week
    this.syncToUrl({param: 'customerId', urlParam: 'customer', initFromRoute: true});
    this.syncToUrl({param: 'week', urlParam: 'week', initFromRoute: true});
    // links from other pages, and the menu, name the week by its dates (?dates=<from>&dates=<to>)
    const linkedDates = [].concat(this.$route.query.dates || []);
    if (!this.$route.query.week && linkedDates.length > 0) {
      this.week = moment(linkedDates.slice().sort()[0]).startOf('week').format(DATE_FORMAT);
    }
  },
  mounted() {
    this.readOnly = !!this.$route.meta.readOnly;
  },
  watch: {
    customer(customer) {
      this.customerId = customer ? customer.uid : null;
    },
    customerId() {
      this.lastChange = null;
      return this.fetchWeek();
    },
    week() {
      this.lastChange = null;
      return this.fetchWeek();
    },
    menuProfileUid() {
      return this.loadBoxCatalog();
    },
  },
  computed: {
    invoice() {
      return this.weekData && this.weekData.invoice;
    },
    deliveryGroups() {
      return groupInvoiceByDelivery(this.invoice, this.weekData ? this.weekData.deliveries : []);
    },
    otherLines() {
      return linesWithoutDelivery(this.invoice);
    },
    canEdit() {
      return !this.readOnly && !!this.weekData && this.weekData.canChange;
    },
    // the household's profiles, each with how it orders: {uid, first_name, last_name, stream, ordersAsBox}
    profiles() {
      return (this.weekData && this.weekData.profiles) || [];
    },
    canSaveQuantity() {
      const isWholeNumber = Number.isInteger(this.newQuantity) && this.newQuantity >= 0;
      return isWholeNumber && !!this.quantityLine && this.newQuantity !== this.quantityLine.quantity;
    },
    lastChangeIsRefund() {
      return !!this.lastChange && this.lastChange.totalAfter < this.lastChange.totalBefore;
    },
    isHouseholdInvoice() {
      return !!this.weekData && this.weekData.billingUid !== this.customerId;
    },
    selectedLines() {
      return selectedCancelLines(this.deliveryGroups, this.cancelQuantities);
    },
    selectedMealCount() {
      return this.selectedLines.reduce((sum, {quantity}) => sum + quantity, 0);
    },
    selectedAmount() {
      return this.selectedLines.reduce((sum, {line, quantity}) => sum + (line.unit_amount || 0) * quantity, 0);
    },
    selectionIsPastEditDeadline() {
      const selectedKeys = new Set(this.selectedLines.map(({line}) => line.key));
      return this.deliveryGroups.some(delivery =>
        delivery.isPastEditDeadline && delivery.lines.some(line => selectedKeys.has(line.key)));
    },
    weekFormatted() {
      const format = 'ddd MMM D';
      return `${moment(this.week).format(format)} - ${moment(this.week).endOf('week').format(format)}`;
    },
    billingUid() {
      return (this.weekData && this.weekData.billingUid) || this.customerId;
    },
    stripeComparisonRoute() {
      return {name: 'CustomerInvoiceComparison', query: {uid: this.billingUid, weekDate: this.week}};
    },
    endOfWeek() {
      return moment(this.week).endOf('week').format(DATE_FORMAT);
    },
    // the profiles whose pause covers part of the week
    pausedProfiles() {
      return this.profiles.filter(profile => {
        const [pauseFrom, pauseTo] = profile.pause || [];
        return !!pauseFrom && !!pauseTo && pauseFrom <= this.endOfWeek && pauseTo >= this.week;
      });
    },
    menuProfile() {
      return this.profiles.find(profile => profile.uid === this.menuProfileUid) || null;
    },
  },
  methods: {
    dateFormatted,
    formatAddress,
    formatCurrency,
    formatOrderType,
    formatDateTime(dateTime) {
      return dateTime ? moment(dateTime).format('ddd MMM D [at] h:mm A') : '';
    },
    setHelpShown(isShown) {
      this.showHelp = isShown;
      try {
        localStorage.setItem(HELP_DISMISSED_KEY, isShown ? 'false' : 'true');
      } catch (e) {
        console.warn('could not remember that the help was dismissed', e);
      }
    },
    nameOf(profile) {
      return [profile.first_name, profile.last_name].filter(s => !!s).join(' ') || profile.uid;
    },
    showMessage(text, color) {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },
    openMenu() {
      const customersProfile = this.profiles.find(profile => profile.uid === this.customerId) || this.profiles[0];
      this.menuProfileUid = customersProfile ? customersProfile.uid : null;
      this.showMenuDialog = true;
      return this.loadBoxCatalog();
    },
    // the menu of a profile on a box plan is the week's box catalog
    async loadBoxCatalog() {
      this.boxCatalog = null;
      this.boxCatalogError = null;
      const profile = this.menuProfile;
      if (!this.showMenuDialog || !profile || !profile.ordersAsBox) {
        return;
      }
      this.loadingBoxCatalog = true;
      try {
        const response = await api.get(`v2/order-edit/${this.customerId}/${this.week}/box-catalog`, {
          params: {account: profile.uid},
        });
        if (this.menuProfileUid === profile.uid) {
          this.boxCatalog = response.data;
        }
      } catch (e) {
        console.error('failed to load the box catalog', e);
        this.boxCatalogError = (e.response && e.response.data && e.response.data.error) || e.message;
      } finally {
        this.loadingBoxCatalog = false;
      }
    },
    pluralMeals(count) {
      return `${count} ${count === 1 ? 'meal' : 'meals'}`;
    },
    totalWithTax(invoice) {
      return invoice ? invoice.total + (invoice.totalTaxes || 0) : 0;
    },
    moveWeek(weeks) {
      this.week = moment(this.week).add(weeks, 'week').format(DATE_FORMAT);
    },
    pickWeekOf(date) {
      this.week = moment(date).startOf('week').format(DATE_FORMAT);
      this.showDatePicker = false;
    },
    async fetchWeek() {
      this.clearSelection();
      this.refusal = null;
      if (!this.customerId || !this.week) {
        this.weekData = null;
        this.loadingRequest = null;
        this.loading = false;
        return;
      }
      // a link that names both the customer and the week sets both, and each asks for the week
      const request = `${this.customerId}/${this.week}`;
      if (this.loadingRequest === request) {
        return;
      }
      this.loadingRequest = request;
      // an answer for a customer or week since left is dropped
      const isStillWanted = () => this.loadingRequest === request;
      this.loading = true;
      try {
        const customerLoading = this.customer && this.customer.uid === this.customerId
          ? Promise.resolve(this.customer)
          : api.get(`v2/customer/${this.customerId}`).then(r => r.data);
        const weekLoading = api.get(`v2/order-edit/${request}`).then(r => r.data);
        const [customer, weekData] = await Promise.all([customerLoading, weekLoading]);
        if (isStillWanted()) {
          this.customer = customer;
          this.weekData = weekData;
        }
      } catch (e) {
        console.error('failed to load the week', e);
        if (isStillWanted()) {
          this.weekData = null;
        }
      } finally {
        if (isStillWanted()) {
          this.loadingRequest = null;
          this.loading = false;
        }
      }
    },
    isSelected(line) {
      return this.cancelQuantities[line.key] > 0;
    },
    quantityChoices(line) {
      return Array.from({length: line.quantity}, (_, i) => i + 1);
    },
    setCancelQuantity(line, quantity) {
      this.$set(this.cancelQuantities, line.key, quantity);
    },
    toggleLine(line) {
      if (this.isSelected(line)) {
        this.$delete(this.cancelQuantities, line.key);
      } else {
        this.setCancelQuantity(line, line.quantity);
      }
    },
    selectDelivery(delivery) {
      delivery.lines.forEach(line => this.setCancelQuantity(line, line.quantity));
    },
    clearSelection() {
      this.cancelQuantities = {};
    },
    reviewCancel() {
      this.review = {
        lines: this.selectedLines,
        mealCount: this.selectedMealCount,
        isPastEditDeadline: this.selectionIsPastEditDeadline,
        leavesPartOfABox: leavesPartOfABox(this.deliveryGroups, this.cancelQuantities),
      };
      this.keepBoxSize = false;
      this.showConfirmDialog = true;
    },
    /**
     * Sends a change of the week's order to the api (`action`: '' to cancel, 'add', 'replace') and shows the
     * invoice it answers with. A refused change has written nothing: the week is loaded again and the reason shown.
     */
    async changeOrder(action, body, summary) {
      const totalBefore = this.totalWithTax(this.invoice);
      this.refusal = null;
      try {
        const url = ['v2/order-edit', this.customerId, this.week, action].filter(part => !!part).join('/');
        const response = await api.post(url, body);
        this.weekData = response.data;
        this.lastChange = {summary, totalBefore, totalAfter: this.totalWithTax(response.data.invoice)};
        this.clearSelection();
      } catch (e) {
        console.error('order change failed', e);
        const wasRefused = e.response && e.response.status === 409;
        // refused: the order no longer matches the invoice shown, so show the current one
        await this.fetchWeek();
        if (wasRefused) {
          this.refusal = e.response.data.error;
        }
      }
    },
    async cancelSelected() {
      const lines = this.review.lines.map(({line, quantity}) => toCancelLine(line, quantity));
      this.cancelling = true;
      try {
        await this.changeOrder('', {lines, sendEmail: this.sendEmail, keepBoxSize: this.keepBoxSize},
          `Cancelled ${this.pluralMeals(this.review.mealCount)}`);
      } finally {
        this.cancelling = false;
        this.showConfirmDialog = false;
      }
    },
    // a plan meal's slot can take a substitute, a box meal another meal; an extra is cancelled and added instead
    canReplace(line) {
      return line.order_type === 'full' || line.order_type === 'box';
    },
    openAddMeal() {
      this.lineToReplace = null;
      this.showMealDialog = true;
    },
    openReplaceMeal(line) {
      this.lineToReplace = line;
      this.showMealDialog = true;
    },
    substitutionLabel(line) {
      if (line.is_allergy_substitution) {
        return 'allergy';
      }
      return line.is_substitution ? 'sub' : '';
    },
    openQuantityDialog(line) {
      this.quantityLine = line;
      this.newQuantity = line.quantity;
      this.keepBoxSize = false;
      this.showQuantityDialog = true;
    },
    // fewer is a cancellation of the difference, more an addition of it
    async changeQuantity() {
      const {quantityLine: line, newQuantity, sendEmail, keepBoxSize} = this;
      const difference = newQuantity - line.quantity;
      const summary = `Changed ${line.description} from ${line.quantity} to ${newQuantity}`;
      this.savingQuantity = true;
      try {
        if (difference < 0) {
          await this.changeOrder('', {lines: [toCancelLine(line, -difference)], sendEmail, keepBoxSize}, summary);
        } else {
          await this.changeOrder('add', {meals: [toAddLine(line, difference)], sendEmail, keepBoxSize}, summary);
        }
      } finally {
        this.savingQuantity = false;
        this.showQuantityDialog = false;
      }
    },
    async addMeals({meals, sendEmail, keepBoxSize}) {
      const added = meals.map(meal => `${meal.quantity} of ${meal.description}`).join(', ');
      this.savingMeal = true;
      try {
        await this.changeOrder('add', {meals, sendEmail, keepBoxSize}, `Added ${added}`);
      } finally {
        this.savingMeal = false;
        this.showMealDialog = false;
      }
    },
    async replaceMeal({line, replacement, sendEmail}) {
      this.savingMeal = true;
      try {
        await this.changeOrder('replace', {line, replacement, sendEmail},
          `Replaced ${line.description} by ${replacement.description}`);
      } finally {
        this.savingMeal = false;
        this.showMealDialog = false;
      }
    },
  },
}
</script>

<style scoped>
/* many columns: less padding than a vuetify table has, and the meal name keeps room to read */
.order-lines th,
.order-lines td {
  padding: 0 8px !important;
}

.order-lines th {
  white-space: nowrap;
}

.meal-column {
  min-width: 13rem;
}

.help-toggle {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}

.select-column {
  width: 48px;
}

.cancel-quantity {
  max-width: 5rem;
}

.line-selected {
  background-color: rgba(244, 67, 54, 0.12);
}
</style>
